import { useLazyQuery, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "App";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { FileSelector } from "components/base/Form";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import Excel from "exceljs";
import { useModals } from "ModalProvider";
import moment from "moment/moment";
import { useContext, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PRICE_EDITOR } from "utils/permissions";
import { byNumber } from "utils/sort";
import XLSX from "xlsx";
import { INC_PRICELIST_ID } from "../consts";
import BatchUpdatePriceView from "./BatchUpdatePriceView";
import DeletePriceListView from "./DeletePriceListView";
import DuplicatePriceListView from "./DuplicatePriceListView";
import EditPriceListView from "./EditPriceListView";
import { FETCH_PRICE_LIST, FETCH_PRODUCT_COSTS } from "./graphql";
import PriceItemForm from "./PriceItemForm";
import PriceListDetail from "./PriceListDetail";

const PriceListDetailPage = () => {
  const { id } = useParams();
  const { hasPermission } = useContext(AppContext);

  const canEdit = hasPermission(PRICE_EDITOR);

  // Special price list, will display odoo related information
  const isInc = parseInt(id) === INC_PRICELIST_ID;

  const { loading, error, data } = useQuery(FETCH_PRICE_LIST, {
    variables: { id },
    fetchPolicy: "network-only",
  });

  const [fetchProductCosts, fetchProductCostsRes] = useLazyQuery(
    FETCH_PRODUCT_COSTS,
    {
      onError: (error) => {
        window.alert(
          `Fail to fetch product costs. Error: ${parseError(error)}`,
        );
      },
    },
  );

  useEffect(() => {
    if (data) {
      const productIds = data.priceList.latestPrices.map(
        (price) => price.product.id,
      );
      if (productIds.length === 0) return;
      fetchProductCosts({
        variables: {
          ids: productIds,
          company: data.priceList.company,
          currency: data.priceList.currency,
        },
      });
    }
  }, [data]);

  const modal = useModals();
  const batchUpdatePriceModal = useModals();
  const navigate = useNavigate();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const productCosts = fetchProductCostsRes.data?.products?.results;

  const currency = data.priceList.currency;

  const products = data.priceList.latestPrices
    .map((price) => {
      const found = productCosts
        ? productCosts.find((x) => x.id === price.product.id)
        : null;
      let margin = null;
      if (price && found) {
        margin = price.useFixedMargin
          ? price.fixedMargin
          : ((price.finalPrice - found.productCost.cost) / price.finalPrice) *
            100;
      }

      return {
        ...price.product,
        productCost: found?.productCost,
        price,
        margin,
      };
    })
    .sort(byNumber);

  console.log("products", products);

  function editPriceList() {
    modal.present({
      title: "Edit Price List",
      center: true,
      children: (
        <EditPriceListView
          id={id}
          complete={modal.hide}
          sourcePriceListId={id}
          initialName={data.priceList.name}
          initialCurrency={data.priceList.currency}
          initialOdooId={data.priceList.odooId}
        />
      ),
    });
  }

  function addProduct() {
    modal.present({
      title: "Add Product",
      subtitle: `Price List: ${data.priceList.name}`,
      center: true,
      children: (
        <PriceItemForm
          company={data.priceList.company}
          priceListId={id}
          currency={currency}
          onSaved={modal.hide}
          isInc={isInc}
        />
      ),
    });
  }

  function editProduct({
    product,
    price,
    discountPercentage,
    useFixedMargin,
    fixedMargin,
    state,
  }) {
    modal.present({
      title: "Edit Price",
      center: true,
      children: (
        <PriceItemForm
          company={data.priceList.company}
          priceListId={id}
          currency={currency}
          onSaved={modal.hide}
          initialData={{
            product,
            price,
            discountPercentage,
            useFixedMargin,
            fixedMargin,
            state,
          }}
        />
      ),
    });
  }

  function duplicatePriceList() {
    modal.present({
      title: "Duplicate Price List",
      center: true,
      children: (
        <DuplicatePriceListView
          complete={duplicateCompleteHandler}
          sourcePriceListId={id}
          initialName={`duplicated ${data.priceList.name}`}
        />
      ),
    });
  }

  function duplicateCompleteHandler(newPriceList) {
    modal.hide();
    navigate(`/price/price-list/${newPriceList.id}`);
  }

  function tryDelete() {
    modal.present({
      title: "Delete Price List",
      center: true,
      children: (
        <DeletePriceListView
          complete={deleteCompleteHandler}
          cancel={modal.hide}
          priceListId={id}
        />
      ),
    });
  }

  function deleteCompleteHandler() {
    modal.hide();
    navigate("/sales/price-lists/");
  }

  function exportPriceList() {
    let rows = [
      [
        "Product ID",
        "Number",
        "Name",
        "Cost (USD)",
        "Cost Updated at",
        `Price (${currency})`,
        "Discount %",
        "Price Updated at",
        "Margin %",
        "Use Fixed Margin(Y/N)",
        "Fixed Margin %",
      ],
    ];
    products.forEach((i) => {
      rows.push([
        i.id,
        i.number,
        i.name,
        i.productCost.cost,
        i.productCost.date,
        i.price.finalPrice,
        i.price.discountPercentage,
        moment(i.price.updatedAt).format("YYYY-MM-DD"),
        i.margin,
        i.price.useFixedMargin ? "Y" : "N",
        i.price.fixedMargin,
      ]);
    });
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "price list");
    XLSX.writeFile(wb, data.priceList.name + ".xlsx");
  }

  const xlsxReader = new FileReader();
  const wb = new Excel.Workbook();

  xlsxReader.onload = function (e) {
    wb.xlsx
      .load(e.target.result)
      .then(handleImport)
      .catch((error) => {
        Alert("error", `Fail to read xlsx file. Error: ${error}`);
      });
  };

  function handleImport(wb) {
    const ws = wb.getWorksheet("price list");
    // read original data
    const rows = [];
    for (let i = 2; i <= ws.rowCount; i++) {
      const row = ws.getRow(i);
      let id = "UNKNOWN";
      let number = "";
      let name = "";
      let price = 0;
      let discount = 0;
      let margin = 0;
      let useFixedMargin = false;
      let fixedMargin = 0;
      try {
        id = row.getCell("A").text;
        number = row.getCell("B").text;
        name = row.getCell("C").text;
        price = parseFloat(row.getCell("F").text);
        discount = parseFloat(row.getCell("G").text);
        margin = parseFloat(row.getCell("I").text);
        useFixedMargin = row.getCell("J").text === "Y";
        fixedMargin = parseFloat(row.getCell("K").text);
      } catch (error) {
        Alert(
          "error",

          `Fail to get value at row ${i}. Error: ${error}, the file may be corrupted.`,
        );
      }
      rows.push({
        id,
        number,
        name,
        price,
        discount,
        margin,
        useFixedMargin,
        fixedMargin,
      });
    }
    // console.log(rows);
    batchUpdatePriceModal.present({
      title: "Import New Prices",
      subtitle: data.priceList.name,
      children: (
        <BatchUpdatePriceView
          priceListId={id}
          rows={rows}
          hide={batchUpdatePriceModal.hide}
        />
      ),
      maxWidth: "max-w-6xl",

      center: true,
      isBeingPresented: true,
    });
  }

  return (
    <Page
      title={`Price List: [${data.priceList.company}] ${data.priceList.name} [${currency}]`}
      backTo="./.."
      className="h-screen"
      rightButtons={
        canEdit && (
          <div className="flex space-x-6">
            <Button onClick={addProduct}>+ Add Product</Button>
            <Button onClick={editPriceList}>Edit</Button>

            <Button onClick={duplicatePriceList}>Duplicate</Button>
            <FileSelector
              bold
              title="Import"
              accept=".xlsx"
              onChange={async (e) => {
                const file = e.target.files[0];
                xlsxReader.readAsArrayBuffer(file);
                e.target.value = null;
              }}
            />
            <Button onClick={exportPriceList}>Export</Button>
            <a
              href={
                process.env.REACT_APP_SERVER_ADMIN_URL +
                "product/pricelist/" +
                id
              }
              target="_blank"
              rel="noreferrer"
            >
              Admin
            </a>
            <Button danger onClick={tryDelete}>
              Delete
            </Button>
          </div>
        )
      }
    >
      <div className="relative flex-1 overflow-auto">
        <div className="grid grid-cols-12 gap-6 h-full flex-1 overflow-auto p-6">
          <div className="col-span-9 h-full overflow-auto">
            <PriceListDetail
              priceListId={id}
              company={data.priceList.company}
              priceListOdooId={data.priceList.odooId}
              products={products}
              currency={currency}
              editProduct={canEdit ? editProduct : null}
              editPriceList={editPriceList}
              isInc={isInc}
            />
          </div>

          <div className="col-span-3 h-full overflow-auto space-y-6">
            <div className="card">
              <h5>Customers</h5>
              <div className="mt-1">
                {data.priceList.customers.length > 0 ? (
                  data.priceList.customers.map((i, index) => (
                    <div
                      key={index}
                      className="border-b border-gray-100 dark:border-gray-700 py-2"
                    >
                      <Link to={`/sales/customers/${i.id}`} target="_blank">
                        {i.name}
                      </Link>
                    </div>
                  ))
                ) : (
                  <div className="opacity-70">
                    No customers using this price list.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default PriceListDetailPage;
